import React, { useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import CodeField from './CodeField';
import TotpEnrollment from './TotpEnrollment';
import { friendlyAuthError, isExpiredSession } from '../cognito';

const PASSWORD_HINT = 'At least 12 characters, with upper and lower case letters, a number and a symbol.';

// Full-page sign-in: email + password, then (as Cognito asks) a new password,
// authenticator pairing, or a 6-digit code. Calls onSignedIn() when done.
const SignInFlow = ({ auth, appName, onSignedIn }) => {
  const [step, setStep] = useState('login'); // login | newPassword | mfaSetup | mfa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Moves to whatever screen Cognito asks for next.
  const advance = async ({ step: next, user: nextUser }) => {
    setUser(nextUser);
    setCode('');
    if (next === 'done') { onSignedIn(); return; }
    if (next === 'mfaSetup') setSecret(await auth.startTotpEnrollment(nextUser));
    setStep(next);
  };

  const submit = (action) => async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await advance(await action());
    } catch (err) {
      setError(friendlyAuthError(err));
      if (isExpiredSession(err)) { setStep('login'); setCode(''); }
    } finally {
      setBusy(false);
    }
  };

  const button = (idle, working) => (
    <Button fullWidth type="submit" variant="contained" disabled={busy} sx={{ mt: 2 }}>
      {busy ? working : idle}
    </Button>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 440, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom>{appName}</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}

        {step === 'login' && (
          <form onSubmit={submit(() => auth.signIn(email, password))}>
            <TextField fullWidth label="Email" type="email" autoComplete="username" value={email}
              onChange={(e) => setEmail(e.target.value)} margin="normal" required autoFocus />
            <TextField fullWidth label="Password" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} margin="normal" required />
            {button('Sign in', 'Signing in…')}
          </form>
        )}

        {step === 'newPassword' && (
          <form onSubmit={submit(() => auth.completeNewPassword(user, newPassword))}>
            <Typography variant="body2" sx={{ mb: 1 }}>Choose a new password for your account.</Typography>
            <TextField fullWidth label="New password" type="password" autoComplete="new-password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} margin="normal" required autoFocus helperText={PASSWORD_HINT} />
            {button('Set password', 'Saving…')}
          </form>
        )}

        {step === 'mfaSetup' && (
          <form onSubmit={submit(() => auth.finishTotpEnrollment(user, code))}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Two-factor authentication is required. Pair an authenticator app to finish signing in.
            </Typography>
            <TotpEnrollment issuer={appName} account={email.trim()} secret={secret} code={code} onCodeChange={setCode} />
            {button('Turn on two-factor', 'Verifying…')}
          </form>
        )}

        {step === 'mfa' && (
          <form onSubmit={submit(() => auth.confirmMfa(user, code))}>
            <Typography variant="body2">Enter the 6-digit code from your authenticator app.</Typography>
            <CodeField value={code} onChange={setCode} autoFocus id="mfa-code" />
            {button('Verify', 'Verifying…')}
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default SignInFlow;
